import { useEffect, useMemo, useState } from 'react';

import { Counter } from '@ui/counter';
import { ToastLinkDescription } from '@/components/ui/toast';
import { Toaster } from '@/components/ui/toaster';
import { DataTableFooter } from '@components/DataTable/DataTableFooter';
import { DataTableShortcuts } from '@components/DataTable/DataTableShortcuts';
import { Button } from '@/components/ui/button';
import { DataTableTabs } from '@components/DataTable/DataTableTabs';
import type { DataTableTab } from '@components/DataTable/types';
import { PlusIcon } from '@components/icons';
import { toast } from '@/hooks/use-toast';
import { useDebouncedValue } from '@hooks/useDebouncedValue';
import { useDiscounts } from '@hooks/useDiscounts';
import { useDeleteDiscount } from '@hooks/discounts';
import { searchQueryParam } from '@/react/utils/searchQuery';
import { usePriceModifiersRouterSearch, type PriceModifierTab } from '@hooks/usePriceModifiersRouterSearch';
import { useSurcharges } from '@hooks/useSurcharges';
import { useDeleteSurcharge } from '@hooks/surcharges';
import { useTranslation } from '@hooks/useTranslation';
import { SEARCH_BAR_WIDTH } from '../../constants';

import { can, CAPABILITIES, type CurrentUser } from '../../lib/capabilities';
import { Page, PageTitle } from '../layouts/Page';
import { PageHeaderTotalLabel, PageHeaderSearchBar } from '../layouts/PageHeader';
import { ResponsivePageHeader } from '../layouts/ResponsivePageHeader';
import { openMobileSidebar } from '../MobileSidebar/mobileSidebarEvents';
import { CreatePriceModifierDialog, type CreatedPriceModifier } from './CreatePriceModifierDialog';
import { DeletePriceModifierDialog } from './DeletePriceModifierDialog';
import { PriceModifiersTable } from './PriceModifiersTable';
import type { PriceModifierRow } from './columns';
import { PriceModifierDetailSheet } from './Sections/PriceModifierDetailSheet';

type PriceModifierSelection = { kind: 'discount' | 'surcharge'; id: number };

const EMPTY_ROWS: readonly PriceModifierRow[] = [];

export default function PriceModifiers({ currentUser }: { currentUser?: CurrentUser }) {
  const router = usePriceModifiersRouterSearch();
  const { t } = useTranslation();
  const { tab: activeTab, q: routerQuery, page, perPage } = router.search;

  // RBAC: write affordances (Add, row Edit, drawer Edit) require
  // price_modifiers.write. Read-only users see the list + detail drawer but no
  // mutation entry points. The server policy stays the source of truth.
  const canWrite = can(currentUser, CAPABILITIES.PRICE_MODIFIERS_WRITE);
  // Editing is owner-scoped: a writer may only edit price modifiers they own
  // (the server policy enforces this; the UI hides Edit for non-owned records)
  // — except admins and sales admins, who manage every record.
  const currentUserId = currentUser?.id;
  const canManageAll = currentUser?.role === 'admin' || currentUser?.role === 'sales_admin';

  const tabs: readonly DataTableTab<PriceModifierTab>[] = useMemo(
    () => [
      { key: 'discounts', label: t('tabs.discounts') },
      { key: 'surcharges', label: t('tabs.surcharges') },
    ],
    [t],
  );

  const emptyTitles: Record<PriceModifierTab, string> = useMemo(
    () => ({
      discounts: t('empty.no_discounts'),
      surcharges: t('empty.no_surcharges'),
    }),
    [t],
  );

  // Local input state so typing stays responsive; the debounced value gets
  // pushed up to the URL (and from there into the API params).
  const [searchInput, setSearchInput] = useState(routerQuery);
  const debouncedSearch = useDebouncedValue(searchInput);

  useEffect(() => {
    const trimmed = debouncedSearch.trim();
    if (trimmed !== routerQuery) {
      router.setQuery(trimmed);
    }
  }, [debouncedSearch, routerQuery, router]);

  useEffect(() => {
    setSearchInput(routerQuery);
  }, [routerQuery]);

  const [selection, setSelection] = useState<PriceModifierSelection | null>(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  // Delete target captures kind+id at click time (derived open state), so a
  // tab change while the dialog is open (e.g. browser back/forward) can't
  // redirect the confirm to the wrong resource kind. Mirrors LocationManagement.
  const [deleteTarget, setDeleteTarget] = useState<PriceModifierSelection | null>(null);

  // Both hooks are declared unconditionally (Rules of Hooks); the captured
  // target's kind selects which one drives the confirmation dialog, so each
  // invalidates its own list on success.
  const deleteDiscount = useDeleteDiscount({ onSuccess: () => setDeleteTarget(null) });
  const deleteSurcharge = useDeleteSurcharge({ onSuccess: () => setDeleteTarget(null) });
  const activeDelete = deleteTarget?.kind === 'surcharge' ? deleteSurcharge : deleteDiscount;

  const handleRowClick = (row: PriceModifierRow) => {
    setSelection({
      kind: activeTab === 'discounts' ? 'discount' : 'surcharge',
      id: row.id,
    });
  };
  const handleAdd = () => setIsCreateDialogOpen(true);
  const handleEdit = (row: PriceModifierRow) => {
    const pluralPath = activeTab === 'discounts' ? 'discounts' : 'surcharges';
    window.location.href = `/${pluralPath}/${row.id}/edit`;
  };
  const handleDelete = (row: PriceModifierRow) =>
    setDeleteTarget({ kind: activeTab === 'discounts' ? 'discount' : 'surcharge', id: row.id });
  // mutate (not mutateAsync): a failed delete keeps the dialog open with its
  // error toast rather than tearing down the confirmation UI.
  const handleDeleteConfirm = () => {
    if (deleteTarget !== null) activeDelete.mutate(deleteTarget.id);
  };

  const queryParams = useMemo(
    () => ({
      page,
      per_page: perPage,
      q: searchQueryParam(routerQuery),
      sort_by: 'name',
      sort_direction: 'asc' as const,
    }),
    [page, perPage, routerQuery],
  );
  const discountsQuery = useDiscounts({ params: queryParams });
  const surchargesQuery = useSurcharges({ params: queryParams });

  const discountsTotal = discountsQuery.data?.pagination.total ?? 0;
  const surchargesTotal = surchargesQuery.data?.pagination.total ?? 0;
  const combinedTotal = discountsTotal + surchargesTotal;

  const activeRows = useMemo<readonly PriceModifierRow[]>(() => {
    if (activeTab === 'discounts') return discountsQuery.data?.discounts ?? EMPTY_ROWS;
    return surchargesQuery.data?.surcharges ?? EMPTY_ROWS;
  }, [activeTab, discountsQuery.data, surchargesQuery.data]);

  const activeIsLoading = activeTab === 'discounts' ? discountsQuery.isLoading : surchargesQuery.isLoading;
  const activeIsFetching = activeTab === 'discounts' ? discountsQuery.isFetching : surchargesQuery.isFetching;
  const activeTotal = activeTab === 'discounts' ? discountsTotal : surchargesTotal;

  const selectedRecord = useMemo(() => {
    if (selection === null) return null;
    if (selection.kind === 'discount' && activeTab !== 'discounts') return null;
    if (selection.kind === 'surcharge' && activeTab !== 'surcharges') return null;
    if (selection.kind === 'discount') {
      return discountsQuery.data?.discounts.find((d) => d.id === selection.id) ?? null;
    }
    return surchargesQuery.data?.surcharges.find((s) => s.id === selection.id) ?? null;
  }, [selection, activeTab, discountsQuery.data, surchargesQuery.data]);

  // Close the drawer if the selected record drops out of the list
  // (filter/tab/search/page change).
  useEffect(() => {
    if (selection !== null && selectedRecord === null) {
      setSelection(null);
    }
  }, [selection, selectedRecord]);

  const totalPages = Math.max(1, Math.ceil(activeTotal / perPage));

  return (
    <>
      <Toaster />
      <Page>
        <Page.Header className="py-6 md:py-0">
          <ResponsivePageHeader
            mobile={{
              title: t('header.title'),
              count: combinedTotal,
              searchValue: searchInput,
              onSearchChange: setSearchInput,
              searchPlaceholder: t('search.placeholder'),
              searchAriaLabel: t('search.aria_label'),
              openNavigationAriaLabel: t('header.open_navigation'),
              openSearchAriaLabel: t('header.open_search'),
              closeSearchAriaLabel: t('header.close_search'),
              onOpenNavigation: openMobileSidebar,
            }}
          >
            <div className="shrink-0">
              <PageHeaderTotalLabel
                count={combinedTotal}
                label={t('header.total')}
              />
            </div>
            <div
              className="flex-1 min-w-0 flex justify-center"
              style={{ flexBasis: SEARCH_BAR_WIDTH }}
            >
              <PageHeaderSearchBar
                value={searchInput}
                onChange={setSearchInput}
                placeholder={t('search.placeholder')}
                ariaLabel={t('search.aria_label')}
              />
            </div>
            <div
              className="min-w-0 invisible"
              aria-hidden="true"
              style={{ flexShrink: 99 }}
            >
              <PageHeaderTotalLabel
                count={combinedTotal}
                label={t('header.total')}
              />
            </div>
          </ResponsivePageHeader>
        </Page.Header>
        <Page.Body>
          <DataTableShortcuts>
            <div className="flex flex-col gap-4 h-full min-h-0 px-6 sm:px-8 lg:px-16">
              <PageTitle>
                <h1 className="font-kapra-expanded text-2xl font-black uppercase leading-[1.5] text-off-black">
                  {t('header.title')}
                </h1>
                <Counter value={activeIsLoading ? '…' : combinedTotal.toLocaleString()} data-loading={activeIsLoading || activeIsFetching} />
              </PageTitle>
              <div className="flex flex-wrap items-center justify-between gap-4 flex-shrink-0">
                <DataTableTabs<PriceModifierTab>
                  tabs={tabs}
                  value={activeTab}
                  onValueChange={router.setTab}
                />
                {canWrite && (
                  <Button onClick={handleAdd}>
                    <PlusIcon className="size-5" aria-hidden="true" />
                    {t('actions.add_price_modifier')}
                  </Button>
                )}
              </div>

              <PriceModifiersTable
                data={activeRows}
                isLoading={activeIsLoading}
                isFetching={activeIsFetching}
                emptyTitle={emptyTitles[activeTab]}
                onRowClick={handleRowClick}
                onEdit={handleEdit}
                onDelete={handleDelete}
                canWrite={canWrite}
                currentUserId={currentUserId}
                canManageAll={canManageAll}
                className="flex-1 min-h-0"
              />
            </div>
          </DataTableShortcuts>
        </Page.Body>
        <Page.Footer>
          <DataTableFooter
            currentPage={Math.min(page, totalPages)}
            totalPages={totalPages}
            totalCount={activeTotal}
            perPage={perPage}
            onPageChange={router.setPage}
            onPerPageChange={router.setPerPage}
            className="flex flex-col justify-center h-full px-6 sm:px-8 lg:px-16"
          />
        </Page.Footer>
      </Page>

      <PriceModifierDetailSheet
        record={selectedRecord}
        kind={selection?.kind ?? null}
        open={selection !== null}
        showEdit={canWrite && (canManageAll || selectedRecord?.user?.id === currentUserId)}
        onOpenChange={(open) => {
          if (!open) setSelection(null);
        }}
      />

      <DeletePriceModifierDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
        onConfirm={handleDeleteConfirm}
        isDeleting={activeDelete.isPending}
      />

      <CreatePriceModifierDialog
        isOpen={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
        initialCategory={activeTab === 'discounts' ? 'discount' : 'surcharge'}
        lockCategory
        onSuccess={(created: CreatedPriceModifier) => {
          const pluralPath = created.category === 'discount' ? 'discounts' : 'surcharges';
          const { dismiss } = toast({
            description: (
              <ToastLinkDescription
                name={created.name}
                href={`/${pluralPath}/${created.modifier_id}/edit`}
                label={t('common.toast.created')}
                onNavigate={() => dismiss()}
              />
            ),
          });
        }}
      />
    </>
  );
}
